import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Briefcase, FileText, Calendar, CheckCircle2, Clock, 
  TrendingUp, Users, Award, Plus, Eye 
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { toast } from 'sonner';
import { format } from 'date-fns';

const LawyerDashboard = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'lawyer') {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, casesRes] = await Promise.all([
        api.get('/stats/dashboard'),
        api.get('/cases')
      ]);
      setStats(statsRes.data);
      setCases(casesRes.data.slice(0, 8));
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const lawyerStats = [
    {
      title: i18n.language === 'ar' ? 'القضايا المسندة' : 'Assigned Cases',
      value: stats?.assigned_cases || 0,
      icon: Briefcase,
      color: 'from-blue-500 to-blue-600',
      change: '+5'
    },
    {
      title: i18n.language === 'ar' ? 'القضايا النشطة' : 'Active Cases',
      value: stats?.active_cases || 0,
      icon: TrendingUp,
      color: 'from-green-500 to-green-600',
      change: '+3'
    },
    {
      title: i18n.language === 'ar' ? 'المذكرات' : 'Memos',
      value: stats?.total_memos || 0,
      icon: FileText,
      color: 'from-purple-500 to-purple-600',
      change: '+12'
    },
    {
      title: i18n.language === 'ar' ? 'معدل النجاح' : 'Success Rate',
      value: '96%',
      icon: Award,
      color: 'from-yellow-500 to-yellow-600',
      change: '+2%'
    },
  ];

  const upcomingTasks = [
    { task: i18n.language === 'ar' ? 'تقديم مذكرة - قضية #2024-001' : 'Submit memo - Case #2024-001', due: '2 days', priority: 'high' },
    { task: i18n.language === 'ar' ? 'جلسة محكمة - قضية #2024-005' : 'Court hearing - Case #2024-005', due: '3 days', priority: 'high' },
    { task: i18n.language === 'ar' ? 'مراجعة عقد - عميل جديد' : 'Contract review - New client', due: '5 days', priority: 'medium' },
  ];

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" data-testid="lawyer-dashboard">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="relative overflow-hidden rounded-3xl p-8 md:p-12" style={{ background: 'linear-gradient(135deg, #1F2937 0%, #2C3E50 50%, #9D7E4F 100%)' }}>
            <div className="relative z-10">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
                {i18n.language === 'ar' ? `مرحباً محامي ${user?.full_name}` : `Welcome Lawyer ${user?.full_name}`}
              </h1>
              <p className="text-white/80 text-lg mb-4">
                {i18n.language === 'ar' ? 'لوحة تحكم المحامي' : 'Lawyer Control Panel'}
              </p>
              <Badge className="bg-white/20 text-white border-white/30 px-4 py-2">
                {i18n.language === 'ar' ? 'محامي معتمد' : 'Certified Lawyer'}
              </Badge>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {lawyerStats.map((stat, index) => (
              <Card key={index} className="relative overflow-hidden border-0 shadow-lg">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                <CardContent className="pt-6 relative">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-sm text-text-secondary mb-2">{stat.title}</p>
                      <p className="text-3xl font-bold">{stat.value}</p>
                    </div>
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                      <stat.icon className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-600">{stat.change}</span>
                    <span className="text-sm text-text-secondary">{i18n.language === 'ar' ? 'هذا الشهر' : 'this month'}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* My Cases */}
            <Card className="lg:col-span-2 shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{i18n.language === 'ar' ? 'قضاياي النشطة' : 'My Active Cases'}</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => navigate('/cases')}>
                    {i18n.language === 'ar' ? 'عرض الكل' : 'View All'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {cases.length === 0 ? (
                  <div className="text-center py-12">
                    <Briefcase className="w-12 h-12 text-text-secondary mx-auto mb-4" />
                    <p className="text-text-secondary">{i18n.language === 'ar' ? 'لا توجد قضايا' : 'No cases'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {cases.slice(0, 5).map((caseItem) => (
                      <div 
                        key={caseItem.id} 
                        className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/cases/${caseItem.id}`)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold">{caseItem.title}</p>
                            <Badge variant="outline" className="text-xs">{caseItem.status}</Badge>
                          </div>
                          <p className="text-sm text-text-secondary">{caseItem.case_number}</p>
                        </div>
                        <Button size="sm" variant="ghost">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Tasks */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle>{i18n.language === 'ar' ? 'المهام القادمة' : 'Upcoming Tasks'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {upcomingTasks.map((task, index) => (
                    <div key={index} className="p-3 border rounded-lg">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <p className="text-sm font-medium line-clamp-2">{task.task}</p>
                        <Badge variant={task.priority === 'high' ? 'destructive' : 'secondary'} className="text-xs">
                          {task.priority}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-text-secondary">
                        <Clock className="w-3 h-3" />
                        <span>{task.due}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle>{i18n.language === 'ar' ? 'إجراءات سريعة' : 'Quick Actions'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Button variant="outline" className="h-24 flex-col gap-2 border-2 hover:border-primary">
                  <Plus className="w-6 h-6" />
                  <span>{i18n.language === 'ar' ? 'مذكرة جديدة' : 'New Memo'}</span>
                </Button>
                <Button variant="outline" className="h-24 flex-col gap-2 border-2 hover:border-primary">
                  <Calendar className="w-6 h-6" />
                  <span>{i18n.language === 'ar' ? 'جلسة جديدة' : 'New Hearing'}</span>
                </Button>
                <Button variant="outline" className="h-24 flex-col gap-2 border-2 hover:border-primary">
                  <FileText className="w-6 h-6" />
                  <span>{i18n.language === 'ar' ? 'رفع مستند' : 'Upload Document'}</span>
                </Button>
                <Button variant="outline" className="h-24 flex-col gap-2 border-2 hover:border-primary">
                  <Users className="w-6 h-6" />
                  <span>{i18n.language === 'ar' ? 'العملاء' : 'Clients'}</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default LawyerDashboard;